import React, { FC, PropsWithChildren } from 'react';
import {
  AntdSchemaFormAdapters,
  AntdSchemaFormValidationAdapters,
} from '@sezenta/antd-schema-form-adapters';
import Form, { MapProps } from '@sezenta/antd-schema-form';
import { FileUpload } from './FileUpload/FileUpload';
import { EnumInput } from './EnumInput';
import { UploadInput } from './FileUpload/UploadInput';
import {
  InputNumber,
  TimePicker,
  ColorPicker,
  Select,
  Input,
  DatePicker,
  Switch,
} from 'antd';
import { RichTextInput } from './RichText/RichTextInput';
import dayjs from 'dayjs';
import { Times } from './times';
import 'dayjs/locale/sv';
import 'dayjs/locale/si';
import 'dayjs/locale/fr-ch';
import { useTranslation } from 'react-i18next';
import { DaySelect } from './DaySelect';
import { StringListInput } from './StringListInput';
import { Utils } from './Utils';
import PermissionSelect from './PermissionSelect';
import TagSelect from './TagSelect';
import { MinMaxInput } from './MinMaxInput';
import ContactPersonsInput from './ContactPersonsInput';
import { StateLabel } from './StateLabel/StateLabel';

export const FormConfigProvider: FC<PropsWithChildren> = (props) => {
  const { i18n } = useTranslation();
  const { language } = i18n;
  dayjs.locale(
    language === 'fr_FR'
      ? 'fr-ch'
      : language === 'sv_SE'
        ? 'sv'
        : language === 'si_LK'
          ? 'si'
          : 'en',
  );
  const times = Times.map((t) => ({
    label: dayjs(t, 'HH:mm').format(Utils.DEFAULT_TIME_FORMAT),
    value: t,
  }));
  return (
    <Form.ConfigProvider
      adapters={{
        ...AntdSchemaFormAdapters,
        select2: Select,
        switch2: Switch,
        file: FileUpload,
        enum: EnumInput,
        state: StateLabel,
        tagSelect: TagSelect,
        upload: UploadInput,
        permission: PermissionSelect,
        price: MapProps(InputNumber, {
          put: { style: { width: '100%', precision: 2 } },
          encode: (value) =>
            value !== undefined ? Math.round(value * 100) : undefined,
          decode: (value) => (value !== undefined ? value / 100 : undefined),
        }),
        weight: MapProps(InputNumber, {
          encode: (value) =>
            value !== undefined ? Math.round(value * 1000) : undefined,
          decode: (value) => (value !== undefined ? value / 1000 : undefined),
          style: { width: '100%' },
        }),
        cm: MapProps(InputNumber, {
          encode: (value) =>
            value !== undefined ? Math.round(value * 10) : undefined,
          decode: (value) => (value !== undefined ? value / 10 : undefined),
          style: { width: '100%' },
        }),
        richText: RichTextInput,
        timex: MapProps(TimePicker, {
          put: { format: 'HH:mm' },
          encode: (value: any) => {
            // Check if value is a valid dayjs object before calling format
            if (value && typeof value.format === 'function') {
              return value.format('HH:mm');
            }
            // Return the original value if it's a string, or undefined otherwise
            return typeof value === 'string' ? value : undefined;
          },
          decode: (value: any) => dayjs(value, 'HH:mm'),
          style: { width: '100%' },
        }),
        colorPicker: MapProps(ColorPicker, {
          put: { disabledAlpha: true },
          encode: (value: any) => {
            // Check if value is a valid object with toHexString method
            if (value && typeof value.toHexString === 'function') {
              return value.toHexString();
            }
            // Return the original value if it's a string, or undefined otherwise
            return typeof value === 'string' ? value : undefined;
          },
          decode: (value: any) => value,
        }),
        timeselect: MapProps(Select, {
          put: { showSearch: true, options: times, optionFilterProp: 'label' },
        }),
        creditLimitRange: MapProps(MinMaxInput, {
          encode: (value) => value,
          decode: (value) => value,
        }),
        contactPersons: ContactPersonsInput,
        dateRange: DatePicker.RangePicker,
        days: DaySelect,
        stringlist: StringListInput,
      }}
      validators={[...AntdSchemaFormValidationAdapters]}
    >
      {props.children as any}
    </Form.ConfigProvider>
  );
};
